import React, { useState, useEffect, useMemo } from 'react';
import { itemsAPI, maestrosAPI, warehousesAPI, suppliersAPI } from '@/lib/api';
import { Plus, Pencil, Trash2, Search, Filter, Warehouse, CheckSquare, Save, Power } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ProductosExcelActions } from './ProductosExcelActions';

const UNITS = ['unidad', 'kg', 'g', 'litro', 'ml', 'paquete', 'bandeja', 'caja'];
const PURCHASE_UNITS = ['unidad', 'caja', 'bandeja', 'pack', 'saco', 'bolsa', 'kg', 'litro'];
const TYPES = [
  { value: 'product', label: 'Producto' },
  { value: 'raw_material', label: 'Materia Prima' }
];

const AdminItemsPage = () => {
  const [items, setItems] = useState([]);
  const [maestrosOptions, setMaestrosOptions] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: '',
    item_type: 'product',
    unit: 'unidad',
    current_stock: 0,
    minimum_stock: 0,
    supplier: '',
    maestros: [],
    warehouse_id: '',
    is_active: true,
    unidades_por_caja: '',
    unidad_compra: 'unidad'
  });
  
  // Estado para selección múltiple
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkWarehouseId, setBulkWarehouseId] = useState('');
  const [savingBulk, setSavingBulk] = useState(false);

  useEffect(() => {
    loadItems();
    loadMaestros();
    loadWarehouses();
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await suppliersAPI.getAll(true);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await warehousesAPI.getActive();
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error al cargar bodegas:', error);
    }
  };

  const loadMaestros = async () => {
    try {
      const response = await maestrosAPI.getAll(true);
      setMaestrosOptions(response.data.map(m => m.name));
    } catch (error) {
      console.error('Error al cargar maestros:', error);
    }
  };

  // Extraer lista única de proveedores de los items (para el filtro)
  const supplierFilterOptions = useMemo(() => {
    const uniqueSuppliers = [...new Set(items.map(item => item.supplier || 'Sin asignar'))];
    return uniqueSuppliers.sort();
  }, [items]);

  // Filtrar items por búsqueda, proveedor y bodega
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchQuery || 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.code.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesSupplier = selectedSupplier === 'all' || 
        (item.supplier || 'Sin asignar') === selectedSupplier;
      
      const matchesWarehouse = selectedWarehouse === 'all' || 
        item.warehouse_id === selectedWarehouse;
      
      return matchesSearch && matchesSupplier && matchesWarehouse;
    });
  }, [items, searchQuery, selectedSupplier, selectedWarehouse]);

  // Manejar selección de un item
  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Seleccionar/deseleccionar todos los items filtrados
  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  // Guardar cambio masivo de bodega
  const saveBulkWarehouseChange = async () => {
    if (!bulkWarehouseId || selectedItems.size === 0) {
      toast.error('Selecciona productos y una bodega');
      return;
    }
    
    setSavingBulk(true);
    try {
      const promises = Array.from(selectedItems).map(itemId => 
        itemsAPI.update(itemId, { warehouse_id: bulkWarehouseId })
      );
      await Promise.all(promises);
      
      const warehouse = warehouses.find(w => w.id === bulkWarehouseId);
      toast.success(`${selectedItems.size} productos asignados a ${warehouse?.name}`);
      
      setSelectedItems(new Set());
      setBulkWarehouseId('');
      loadItems();
    } catch (error) {
      toast.error('Error al actualizar productos');
    } finally {
      setSavingBulk(false);
    }
  };

  // Toggle estado activo/inactivo de un producto
  const toggleItemActive = async (itemId, currentActive) => {
    try {
      await itemsAPI.update(itemId, { is_active: !currentActive });
      toast.success(currentActive ? 'Producto desactivado' : 'Producto activado');
      loadItems();
    } catch (error) {
      toast.error('Error al cambiar estado del producto');
    }
  };

  const loadItems = async () => {
    try {
      const response = await itemsAPI.getAll(false);
      setItems(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Limpiar formData antes de enviar
      const dataToSend = {
        ...formData,
        // Convertir unidades_por_caja a número o null si está vacío
        unidades_por_caja: formData.unidades_por_caja === '' || formData.unidades_por_caja === null 
          ? null 
          : parseFloat(formData.unidades_por_caja),
        // Asegurar que campos opcionales vacíos sean null
        supplier: formData.supplier || null,
        description: formData.description || null,
        warehouse_id: formData.warehouse_id || null,
      };
      
      if (editingItem) {
        await itemsAPI.update(editingItem.id, dataToSend);
        toast.success('Producto actualizado');
      } else {
        await itemsAPI.create(dataToSend);
        toast.success('Producto creado');
      }
      setDialogOpen(false);
      loadItems();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      try {
        await itemsAPI.delete(id);
        toast.success('Producto eliminado');
        loadItems();
      } catch (error) {
        toast.error('Error al eliminar producto');
      }
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingItem(null);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code,
      name: item.name,
      description: item.description || '',
      category: item.category,
      item_type: item.item_type,
      unit: item.unit,
      current_stock: item.current_stock,
      minimum_stock: item.minimum_stock,
      supplier: item.supplier || '',
      maestros: item.maestros || [],
      warehouse_id: item.warehouse_id || '',
      is_active: item.is_active,
      unidades_por_caja: item.unidades_por_caja || '',
      unidad_compra: item.unidad_compra || 'unidad'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      category: '',
      item_type: 'product',
      unit: 'unidad',
      current_stock: 0,
      minimum_stock: 0,
      supplier: '',
      maestros: [],
      warehouse_id: warehouses.length > 0 ? warehouses[0].id : '',
      is_active: true,
      unidades_por_caja: '',
      unidad_compra: 'unidad'
    });
  };

  const toggleMaestro = (maestro) => {
    setFormData(prev => ({
      ...prev,
      maestros: prev.maestros.includes(maestro)
        ? prev.maestros.filter(m => m !== maestro)
        : [...prev.maestros, maestro]
    }));
  };

  return (
    <div className="space-y-6" data-testid="admin-items-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Productos</h1>
          <p className="text-muted-foreground mt-1">Gestiona el catálogo de productos</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ProductosExcelActions onApplied={loadItems} />
          <Button onClick={openCreateDialog} data-testid="create-item-button">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nombre o código..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="sm:w-48">
              <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                <SelectTrigger data-testid="warehouse-filter">
                  <Warehouse className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Filtrar por bodega" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las bodegas</SelectItem>
                  {warehouses.map(wh => (
                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:w-48">
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger data-testid="supplier-filter">
                  <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Filtrar por proveedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proveedores</SelectItem>
                  {supplierFilterOptions.map(supplier => (
                    <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(searchQuery || selectedSupplier !== 'all' || selectedWarehouse !== 'all') && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <span>Mostrando {filteredItems.length} de {items.length} productos</span>
              {(searchQuery || selectedSupplier !== 'all' || selectedWarehouse !== 'all') && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setSearchQuery(''); setSelectedSupplier('all'); setSelectedWarehouse('all'); }}
                  className="h-auto py-1 px-2 text-xs"
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Barra de acciones masivas */}
        {selectedItems.size > 0 && (
          <div className="p-4 bg-primary/5 border-b border-slate-200 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-primary" />
              <span className="font-medium text-slate-700">{selectedItems.size} productos seleccionados</span>
            </div>
            <div className="flex items-center gap-2 flex-1">
              <Label className="text-sm whitespace-nowrap">Asignar a bodega:</Label>
              <Select value={bulkWarehouseId} onValueChange={setBulkWarehouseId}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Seleccionar bodega" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(wh => (
                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={saveBulkWarehouseChange} 
                disabled={!bulkWarehouseId || savingBulk}
                size="sm"
              >
                {savingBulk ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </>
                )}
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedItems(new Set())}
              className="text-slate-500"
            >
              Cancelar
            </Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-center w-12">
                  <Checkbox 
                    checked={filteredItems.length > 0 && selectedItems.size === filteredItems.length}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Seleccionar todos"
                  />
                </th>
                <th className="px-4 py-3 text-left font-semibold">Código</th>
                <th className="px-4 py-3 text-left font-semibold">Nombre</th>
                <th className="px-4 py-3 text-left font-semibold">Bodega</th>
                <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
                <th className="px-4 py-3 text-left font-semibold">Maestros</th>
                <th className="px-4 py-3 text-right font-semibold">Stock</th>
                <th className="px-4 py-3 text-center font-semibold">Estado</th>
                <th className="px-4 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const warehouse = warehouses.find(w => w.id === item.warehouse_id);
                const isSelected = selectedItems.has(item.id);
                return (
                <tr key={item.id} className={`border-b hover:bg-slate-50/50 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}>
                  <td className="px-4 py-4 text-center">
                    <Checkbox 
                      checked={isSelected}
                      onCheckedChange={() => toggleItemSelection(item.id)}
                      aria-label={`Seleccionar ${item.name}`}
                    />
                  </td>
                  <td className="px-4 py-4 font-mono text-sm">{item.code}</td>
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-semibold text-slate-900">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                      warehouse?.code === 'BODEGA-MALL' 
                        ? 'bg-purple-50 text-purple-700' 
                        : 'bg-emerald-50 text-emerald-700'
                    }`}>
                      {warehouse?.name || 'Sin asignar'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                      {item.supplier || 'Sin asignar'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(item.maestros && item.maestros.length > 0) ? (
                        item.maestros.map(m => (
                          <span key={m} className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-xs font-medium">
                            {m.replace('Sub-Maestro ', '')}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className={`font-semibold ${
                      item.current_stock < item.minimum_stock ? 'text-red-600' : 'text-slate-900'
                    }`}>
                      {item.current_stock} {item.unit}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <Switch
                        checked={item.is_active}
                        onCheckedChange={() => toggleItemActive(item.id, item.is_active)}
                        aria-label={`${item.is_active ? 'Desactivar' : 'Activar'} ${item.name}`}
                      />
                      <span className={`text-xs font-medium ${
                        item.is_active ? 'text-green-600' : 'text-slate-400'
                      }`}>
                        {item.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(item)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">Descripción</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="category">Categoría *</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="supplier">Proveedor</Label>
                <Select
                  value={formData.supplier || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, supplier: value === 'none' ? '' : value })}
                >
                  <SelectTrigger id="supplier" data-testid="item-supplier-select">
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proveedor</SelectItem>
                    {suppliers.map((sup) => (
                      <SelectItem key={sup.id} value={sup.name}>
                        {sup.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="warehouse_id">Bodega *</Label>
                <Select value={formData.warehouse_id} onValueChange={(value) => setFormData({ ...formData, warehouse_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una bodega" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(wh => (
                      <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="item_type">Tipo *</Label>
                <Select value={formData.item_type} onValueChange={(value) => setFormData({ ...formData, item_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="unit">Unidad *</Label>
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="current_stock">Stock Actual *</Label>
                <Input
                  id="current_stock"
                  type="number"
                  step="0.1"
                  value={formData.current_stock}
                  onChange={(e) => setFormData({ ...formData, current_stock: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="minimum_stock">Stock Mínimo *</Label>
                <Input
                  id="minimum_stock"
                  type="number"
                  step="0.1"
                  value={formData.minimum_stock}
                  onChange={(e) => setFormData({ ...formData, minimum_stock: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              
              {/* Sección de configuración de cajas para compras */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium text-sm text-slate-700 mb-3">Configuración para Pedidos a Proveedores</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="unidades_por_caja">Unidades por Caja</Label>
                    <Input
                      id="unidades_por_caja"
                      type="number"
                      step="1"
                      min="1"
                      value={formData.unidades_por_caja}
                      onChange={(e) => setFormData({ ...formData, unidades_por_caja: e.target.value ? parseFloat(e.target.value) : '' })}
                      placeholder="Ej: 12, 24, 6..."
                    />
                    <p className="text-xs text-slate-500 mt-1">Cuántas unidades contiene una caja de este producto</p>
                  </div>
                  <div>
                    <Label htmlFor="unidad_compra">Unidad de Compra</Label>
                    <Select value={formData.unidad_compra} onValueChange={(value) => setFormData({ ...formData, unidad_compra: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona unidad de compra" />
                      </SelectTrigger>
                      <SelectContent>
                        {PURCHASE_UNITS.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Cómo compras este producto al proveedor</p>
                  </div>
                </div>
              </div>
              
              <div className="col-span-2">
                <Label>Maestros</Label>
                <div className="flex flex-wrap gap-4 mt-2 p-3 bg-slate-50 rounded-md border">
                  {maestrosOptions.length > 0 ? (
                    maestrosOptions.map(maestro => (
                      <div key={maestro} className="flex items-center space-x-2">
                        <Checkbox
                          id={maestro}
                          checked={formData.maestros.includes(maestro)}
                          onCheckedChange={() => toggleMaestro(maestro)}
                        />
                        <label
                          htmlFor={maestro}
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          {maestro}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No hay maestros creados. Créalos en Configuración.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingItem ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminItemsPage;